import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminHeader from '../../components/navigation/AdminHeader';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { supabase } from '../../lib/supabase';
import PhotoEvidenceUpload from './components/PhotoEvidenceUpload';
import PartsTracker from './components/PartsTracker';
import WorkNotes from './components/WorkNotes';
import TimeTracker from './components/TimeTracker';

const ServiceRequestDetailUpdates = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const requestId = routerLocation?.state?.requestId;

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [updating, setUpdating] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    details: true,
    photos: true,
    parts: false,
    notes: false,
    time: false
  });

  useEffect(() => {
    if (!requestId) {
      navigate('/technician-portal-dashboard');
      return;
    }
    fetchRequestDetails();
  }, [requestId]);

  const fetchRequestDetails = async () => {
    try {
      const { data, error } = await supabase
        ?.from('service_requests')
        ?.select('*')
        ?.eq('id', requestId)
        ?.single();

      if (error) throw error;
      setRequest(data);
      setSelectedStatus(data?.status);
    } catch (error) {
      console.error('Error fetching request:', error);
      alert('Failed to load request details.');
      navigate('/technician-portal-dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (selectedStatus === request?.status) return;

    setUpdating(true);
    try {
      const { error } = await supabase
        ?.from('service_requests')
        ?.update({ status: selectedStatus, updated_at: new Date()?.toISOString() })
        ?.eq('id', requestId);

      if (error) throw error;

      setRequest(prev => ({ ...prev, status: selectedStatus }));
      alert('Status updated successfully!');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev?.[section] }));
  };

  const getStatusConfig = (status) => {
    const configs = {
      'pending': { color: 'text-warning', icon: 'Clock', label: 'Pending' },
      'in-progress': { color: 'text-primary', icon: 'Activity', label: 'In Progress' },
      'completed': { color: 'text-success', icon: 'CheckCircle2', label: 'Completed' },
      'cancelled': { color: 'text-muted-foreground', icon: 'XCircle', label: 'Cancelled' }
    };
    return configs?.[status] || configs?.['pending'];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <AdminHeader />
        <main className="pt-16">
          <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
            <div className="text-center">
              <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading request details...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const statusConfig = getStatusConfig(request?.status);

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader />
      <main className="pt-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="icon"
              iconName="ArrowLeft"
              onClick={() => navigate('/technician-portal-dashboard')}
            />
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-foreground">{request?.request_number}</h1>
              <p className="text-sm text-muted-foreground">Created {formatDate(request?.created_at)}</p>
            </div>
            <div className={`flex items-center gap-2 ${statusConfig?.color}`}>
              <Icon name={statusConfig?.icon} size={20} strokeWidth={2} />
              <span className="text-sm font-semibold">{statusConfig?.label}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Request Details */}
              <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
                <button
                  onClick={() => toggleSection('details')}
                  className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-smooth"
                >
                  <div className="flex items-center gap-2">
                    <Icon name="FileText" size={20} color="var(--color-primary)" strokeWidth={2} />
                    <h2 className="text-lg font-semibold text-foreground">Request Details</h2>
                  </div>
                  <Icon name={expandedSections?.details ? 'ChevronUp' : 'ChevronDown'} size={20} strokeWidth={2} />
                </button>
                {expandedSections?.details && (
                  <div className="p-4 space-y-4">
                    {/* Driver Info */}
                    <div>
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Driver Information</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center gap-2">
                          <Icon name="Phone" size={16} color="var(--color-primary)" strokeWidth={2} />
                          <span className="text-sm text-foreground">{request?.driver_phone}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Icon name="Truck" size={16} color="var(--color-primary)" strokeWidth={2} />
                          <span className="text-sm text-foreground">Unit: {request?.unit_number}</span>
                        </div>
                      </div>
                      {request?.vin && (
                        <div className="flex items-center gap-2 mt-2">
                          <Icon name="Hash" size={16} color="var(--color-primary)" strokeWidth={2} />
                          <span className="text-sm text-foreground">VIN: {request?.vin}</span>
                        </div>
                      )}
                      {request?.yard_name && (
                        <div className="flex items-center gap-2 mt-2">
                          <Icon name="MapPin" size={16} color="var(--color-primary)" strokeWidth={2} />
                          <span className="text-sm text-foreground">{request?.yard_name}</span>
                        </div>
                      )}
                    </div>

                    {/* Issue Details */}
                    <div className="border-t border-border pt-4">
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Issue Details</h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Urgency</p>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border ${
                            request?.urgency === 'Emergency' ? 'bg-destructive/10 text-destructive border-destructive/20' : 'bg-warning/10 text-warning border-warning/20'
                          }`}>
                            <Icon name={request?.urgency === 'Emergency' ? 'AlertTriangle' : 'Calendar'} size={12} strokeWidth={2} />
                            {request?.urgency}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Issue Type</p>
                          <p className="text-sm text-foreground">{request?.issue_type}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Description</p>
                          <p className="text-sm text-foreground">{request?.description}</p>
                        </div>
                        {request?.parts_needed && (
                          <div>
                            <p className="text-xs text-muted-foreground mb-1">Parts/Supplies Requested</p>
                            <p className="text-sm text-foreground">{request?.parts_needed}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Location */}
                    {request?.gps_address && (
                      <div className="border-t border-border pt-4">
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Location</h3>
                        <div className="flex items-start gap-2">
                          <Icon name="MapPin" size={16} color="var(--color-primary)" strokeWidth={2} className="flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-foreground">{request?.gps_address}</p>
                        </div>
                        {request?.gps_latitude && request?.gps_longitude && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Coordinates: {request?.gps_latitude}, {request?.gps_longitude}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Submitted Photos */}
                    {request?.photo_urls && request?.photo_urls?.length > 0 && (
                      <div className="border-t border-border pt-4">
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Submitted Photos</h3>
                        <div className="grid grid-cols-3 gap-2">
                          {request?.photo_urls?.map((url, index) => (
                            <img
                              key={index}
                              src={`${import.meta.env?.VITE_SUPABASE_URL}/storage/v1/object/public/service-request-photos/${url}`}
                              alt={`Submitted photo ${index + 1}`}
                              className="w-full h-24 object-cover rounded-lg border border-border cursor-pointer hover:opacity-80 transition-smooth"
                              onClick={() => window.open(`${import.meta.env?.VITE_SUPABASE_URL}/storage/v1/object/public/service-request-photos/${url}`, '_blank')}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Photo Evidence Upload */}
              <PhotoEvidenceUpload requestId={requestId} />

              {/* Parts Tracker */}
              <PartsTracker requestId={requestId} />

              {/* Work Notes */}
              <WorkNotes requestId={requestId} />

              {/* Time Tracker */}
              <TimeTracker requestId={requestId} />
            </div>

            {/* Sidebar - Status Updates */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-lg shadow-elevation-1 p-4 sticky top-20">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="Activity" size={20} color="var(--color-primary)" strokeWidth={2} />
                  Status Update
                </h2>

                <Select
                  label="Current Status"
                  options={statusOptions}
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                />

                {selectedStatus !== request?.status && (
                  <Button
                    variant="default"
                    size="lg"
                    fullWidth
                    className="mt-4"
                    onClick={handleStatusUpdate}
                    loading={updating}
                  >
                    Update Status
                  </Button>
                )}

                <div className="mt-6 space-y-2">
                  <h3 className="text-sm font-semibold text-foreground">Quick Actions</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="Phone"
                    iconPosition="left"
                    onClick={() => window.location.href = `tel:${request?.driver_phone}`}
                  >
                    Call Driver
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    fullWidth
                    iconName="MessageSquare"
                    iconPosition="left"
                    onClick={() => window.location.href = `sms:${request?.driver_phone}`}
                  >
                    Message Driver
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ServiceRequestDetailUpdates;